import { useEffect, useState } from "react";
import api, { API_BASE } from "@/lib/api";
import { Download } from "lucide-react";
import Pagination from "@/components/Pagination";

const PAGE_SIZE = 50;

export default function Audit() {
  const [rows, setRows] = useState([]);
  const [entity, setEntity] = useState("");
  const [action, setAction] = useState("");
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);

  const load = (p = 1) => {
    const params = { page: p, limit: PAGE_SIZE };
    if (entity) params.entity = entity;
    if (action) params.action = action;
    api.get("/audit", { params }).then((r) => {
      setRows(r.data.data); setPage(r.data.page); setPages(r.data.pages); setTotal(r.data.total);
    });
  };
  // Re-run from page 1 whenever the filters change; also on initial mount.
  useEffect(() => { load(1); /* eslint-disable-next-line */ }, [entity, action]);

  const download = async () => {
    const token = localStorage.getItem("accp_token");
    const resp = await fetch(`${API_BASE}/audit/export`, {
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "audit_log.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-8">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="accp-label">Audit mode · APPEND-ONLY</div>
          <h1 className="text-4xl font-semibold text-primary" style={{fontFamily:'Outfit'}}>Audit Log</h1>
          <p className="text-muted-foreground mt-1">Immutable trail. Every mutation recorded with before/after diff. No deletions, no edits. Exportable.</p>
        </div>
        <button onClick={download} className="accp-btn-primary" data-testid="audit-export-btn">
          <Download size={16}/> Export CSV
        </button>
      </div>

      <div className="flex gap-3 flex-wrap">
        <select className="accp-input max-w-[200px]" value={entity} onChange={(e)=>setEntity(e.target.value)}>
          <option value="">All entities</option>
          <option>user</option><option>shift</option><option>timesheet</option>
          <option>certification</option><option>incident</option><option>mar_log</option><option>mar_schedule</option>
        </select>
        <select className="accp-input max-w-[200px]" value={action} onChange={(e)=>setAction(e.target.value)}>
          <option value="">All actions</option>
          <option>create</option><option>update</option><option>delete</option>
          <option>login</option><option>logout</option><option>clock_in</option><option>clock_out</option>
          <option>approve</option><option>reject</option><option>review</option>
        </select>
      </div>

      <div className="accp-card overflow-hidden">
        <div className="accp-table-scroll">
        <table className="w-full text-sm">
          <thead className="bg-accent/30 text-left">
            <tr>
              <th className="p-3 accp-label">Code</th>
              <th className="p-3 accp-label">When</th>
              <th className="p-3 accp-label">User</th>
              <th className="p-3 accp-label">Action</th>
              <th className="p-3 accp-label">Entity</th>
              <th className="p-3 accp-label">Entity Code</th>
              <th className="p-3 accp-label">Severity</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-border">
                <td className="p-3 font-mono text-[11px] text-muted-foreground">{r.code || "—"}</td>
                <td className="p-3 text-muted-foreground font-mono text-[11px]">{new Date(r.timestamp).toLocaleString()}</td>
                <td className="p-3 font-mono text-[11px] text-muted-foreground" title={r.user_name}>{r.user_code || "—"}</td>
                <td className="p-3"><span className="px-2 py-0.5 rounded-full bg-accent text-primary text-xs font-semibold">{r.action}</span></td>
                <td className="p-3 text-primary">{r.entity}</td>
                <td className="p-3 font-mono text-[11px] text-muted-foreground">{r.entity_code || "—"}</td>
                <td className="p-3"><span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${r.severity === 'critical' ? 'compliance-expired' : r.severity === 'warn' ? 'bg-[#FFF8E6] text-[#7A5A00]' : 'bg-accent text-primary'}`}>{r.severity}</span></td>
              </tr>
            ))}
            {rows.length===0 && <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">No events.</td></tr>}
          </tbody>
        </table>
        </div>
        <Pagination page={page} pages={pages} total={total} onChange={load} testId="audit-pagination" />
      </div>
    </div>
  );
}
